import * as React from "react";
import { DayPicker, MonthCaptionProps, useNavigation } from "react-day-picker";
import { format, startOfDay, endOfDay } from "date-fns";
import { parseCalendarDate } from "../../../utilities/dateTime";
import "react-day-picker/dist/style.css";
import "./custom-calendar.css";

import {
    IoIosArrowRoundUp,
    IoIosArrowRoundDown,
    IoIosArrowDown,
} from "react-icons/io";

const ArrowRoundUp = IoIosArrowRoundUp as React.ComponentType;
const ArrowRoundDown = IoIosArrowRoundDown as React.ComponentType;
const ArrowDown = IoIosArrowDown as React.ComponentType;

/* ---------- Custom Caption ---------- */
function CustomMonthCaption({ calendarMonth, yearRange }: MonthCaptionProps & { yearRange?: { from: number; to: number } }) {
    const { goToMonth, nextMonth, previousMonth } = useNavigation();
    const [open, setOpen] = React.useState(false);
    const monthLabel = React.useRef<HTMLButtonElement>(null);

    const BASE_YEAR = new Date().getFullYear();
    const selectedYear = calendarMonth.date.getFullYear();
    const monthIndex = calendarMonth.date.getMonth();

    const years = yearRange ? Array.from({ length: yearRange.to - yearRange.from + 1 }, (_, i) => yearRange.to - i)
        : Array.from({ length: 11 }, (_, i) => (BASE_YEAR - 5) + i);

    return (
        <div className="custom-caption">
            <div className="month-wrapper">
                <button
                    type="button"
                    ref={monthLabel}
                    className="month-label"
                    onClick={() => setOpen(!open)}
                >
                    {format(calendarMonth.date, "MMMM, yyyy")}
                    <ArrowDown />
                </button>

                {open && (
                    <div className="year-dropdown" style={yearRange ? { maxHeight: "min(240px, 50vh)", overflowY: "auto", overscrollBehavior: "contain" } : undefined}>
                        {years.map((year) => (
                            <button
                                type="button"
                                key={year}
                                ref={node => { if (yearRange && year === selectedYear) node?.scrollIntoView?.({ block: "nearest" }); }}
                                className={`year-option ${year === selectedYear ? "active" : ""
                                    }`}
                                onClick={() => {
                                    goToMonth(yearRange ? parseCalendarDate(`${String(year).padStart(4, "0")}-${String(monthIndex + 1).padStart(2, "0")}-01`, true)! : new Date(year, monthIndex));
                                    setOpen(false);
                                    // The chosen option unmounts; keep keyboard focus inside the calendar.
                                    monthLabel.current?.focus();
                                }}
                            >
                                {year}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="nav-buttons">
                <button
                    type="button"
                    aria-label="Previous month"
                    disabled={!previousMonth}
                    onClick={() => { setOpen(false); if (previousMonth) goToMonth(previousMonth); }}
                >
                    <ArrowRoundUp />
                </button>

                <button
                    type="button"
                    aria-label="Next month"
                    disabled={!nextMonth}
                    onClick={() => { setOpen(false); if (nextMonth) goToMonth(nextMonth); }}
                >
                    <ArrowRoundDown />
                </button>
            </div>
        </div>
    );
}

/* ---------- Date Picker ---------- */
interface CustomDatePickerProps {
    value: string;
    onChange: (date: string) => void;
    disabled?: boolean;
    disableFuture?: boolean;
    disablePast?: boolean;
    min?: string;
    max?: string;
    yearRange?: { from: number; to: number };
    /** Optional literal date in the viewer saved zone; ordinary callers retain their legacy device-today behavior. */
    calendarToday?: string;
}

// The caption must keep one component identity across renders; an inline component remounts it on
// every month change and drops keyboard focus from the Previous/Next buttons.
const YearRangeContext = React.createContext<{ from: number; to: number } | undefined>(undefined);
const ContextMonthCaption = (props: MonthCaptionProps) => (
    <CustomMonthCaption {...props} yearRange={React.useContext(YearRangeContext)} />
);
const PICKER_COMPONENTS = { MonthCaption: ContextMonthCaption };

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
    value,
    onChange,
    disabled,
    disableFuture,
    disablePast,
    min,
    max,
    yearRange,
    calendarToday
}) => {
    // FIX: Add state to control the visible month
    const [month, setMonth] = React.useState<Date>(
        parseCalendarDate(value, true) ?? (calendarToday ? parseCalendarDate(calendarToday, true) : null) ?? new Date()
    );

    // FIX: Sync internal month view when external value changes (like when typing)
    React.useEffect(() => {
        if (value) {
            const parsedDate = parseCalendarDate(value, true);
            if (parsedDate) {
                setMonth(parsedDate);
            }
        }
    }, [value]);

    let disabledProp: any = undefined;
    if (disabled) {
        disabledProp = true;
    } else {
        const disabledMatchers: any[] = [];

        if (disableFuture) {
            disabledMatchers.push({ after: endOfDay(new Date()) });
        }
        if (disablePast) {
            disabledMatchers.push({ before: startOfDay(new Date()) });
        }
        if (min && parseCalendarDate(min, true)) {
            disabledMatchers.push({ before: parseCalendarDate(min, true)! });
        }
        if (max && parseCalendarDate(max, true)) {
            disabledMatchers.push({ after: endOfDay(parseCalendarDate(max, true)!) });
        }

        disabledProp = disabledMatchers.length > 0 ? disabledMatchers : undefined;
    }

    const selectedDate = parseCalendarDate(value, true) ?? undefined;
    const stableYearRange = React.useMemo(
        () => (yearRange ? { from: yearRange.from, to: yearRange.to } : undefined),
        [yearRange?.from, yearRange?.to], // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Use the browser's local calendar date so "today" follows the user's
    // device/system timezone rather than the server or UTC date.
    const localToday = React.useMemo(() => {
        if (calendarToday && parseCalendarDate(calendarToday, true)) return parseCalendarDate(calendarToday, true)!;
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }, [calendarToday]);

    return (
        <YearRangeContext.Provider value={stableYearRange}>
        <DayPicker
            mode="single"
            today={localToday}
            selected={selectedDate}
            modifiers={{ localToday }}
            modifiersClassNames={{ localToday: "rdp-local-today" }}
            month={month}
            onMonthChange={setMonth}
            // disabled={disabled ? undefined : { after: new Date() }}
            disabled={disabledProp}
            onSelect={(date) => {
                if (!date) return;
                onChange(format(date, "yyyy-MM-dd"));
            }}
            fromYear={yearRange?.from ?? 2020}
            toYear={yearRange?.to ?? 2035}
            captionLayout="dropdown"
            components={PICKER_COMPONENTS}
            className="custom-calendar"
        />
        </YearRangeContext.Provider>
    );
};

export default CustomDatePicker;
